import React from 'react';
import styled from 'styled-components';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from 'react-beautiful-dnd';
import {useCurrentClientQuery} from 'api';
import {PlusOutlined} from '@ant-design/icons';
import {Button, Select} from 'components/antd';
import {Handle} from 'components/Ui/Icon';
import New from './New';
import Update from './Update';

export default () => {
  const [preleadTags, setPreleadTags] = React.useState([]);
  const [newModalVisible, setNewModalVisible] = React.useState(false);
  const [updateModalItem, setUpdateModalItem] = React.useState(null);
  const [selectedGroupId, setSelectedGroupId] = React.useState<string | null>(null);

  const {data: {currentClient = {}} = {}} = useCurrentClientQuery({});

  const projectGroups = currentClient?.projectGroups || [];
  const allPreleadTags = currentClient?.preleadTags || [];

  // 選択されたグループに応じてタグをフィルタリング
  const filteredTags = React.useMemo(() => {
    if (!selectedGroupId) return allPreleadTags;
    return allPreleadTags.filter(tag => tag.projectGroup?.id === selectedGroupId);
  }, [allPreleadTags, selectedGroupId]);

  const onDragEnd = React.useCallback(
    (result: DropResult) => {
      const sourceIndex = result.source.index;
      const destIndex = result.destination.index;

      const items = [...preleadTags];
      const [removed] = items.splice(sourceIndex, 1);
      items.splice(destIndex, 0, removed);

      setPreleadTags(items);
      // TODO: Add updatePreleadTagPosition mutation when available
    },
    [preleadTags],
  );

  // 初期表示時に最初のグループを選択
  React.useEffect(() => {
    if (projectGroups.length > 0 && !selectedGroupId) {
      setSelectedGroupId(projectGroups[0].id);
    }
  }, [projectGroups, selectedGroupId]);

  React.useEffect(() => {
    setPreleadTags(filteredTags);
  }, [filteredTags]);

  return (
    <div>
      <PageHeader>
        <div>
          <h3>企業のタグを編集、新規追加することができます</h3>
          {projectGroups.length > 0 && (
            <GroupSelector>
              <label>グループ:</label>
              <Select
                value={selectedGroupId}
                onChange={(value) => setSelectedGroupId(value)}
                style={{width: 200}}>
                {projectGroups.map((group) => (
                  <Select.Option key={group.id} value={group.id}>
                    {group.name}
                  </Select.Option>
                ))}
              </Select>
            </GroupSelector>
          )}
        </div>

        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setNewModalVisible(true)}>
          新規作成
        </Button>
      </PageHeader>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {preleadTags.map((preleadTag, index) => (
                <Draggable
                  key={preleadTag.id}
                  draggableId={preleadTag.id}
                  index={index}>
                  {(provided, snapshot) => (
                    <DraggableItem
                      ref={provided.innerRef}
                      onClick={() => setUpdateModalItem(preleadTag)}
                      {...provided.draggableProps}
                      className={snapshot.isDragging ? 'dragging' : ''}>
                      <HandleWrapper {...provided.dragHandleProps}>
                        <Handle />
                      </HandleWrapper>
                      <PreleadTag>
                        <TagRectangle color={preleadTag.color} />
                        <span>{preleadTag.name}</span>
                      </PreleadTag>
                    </DraggableItem>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <New
        visible={newModalVisible}
        onClose={() => setNewModalVisible(false)}
        selectedGroupId={selectedGroupId}
      />
      <Update
        preleadTag={updateModalItem}
        onClose={() => setUpdateModalItem(null)}
      />
    </div>
  );
};

const PageHeader = styled.div`
  display: flex;
  margin-bottom: 1rem;
  align-items: flex-end;

  > div {
    flex: 1;
  }

  h3 {
    font-size: 12px;
    font-weight: 400;
    margin-bottom: 0;
  }
`;

const GroupSelector = styled.div`
  display: flex;
  align-items: center;
  margin-top: 0.5rem;
  gap: 0.5rem;

  label {
    font-weight: 500;
    font-size: 14px;
  }
`;

const DraggableItem = styled.div`
  min-height: 48px;
  box-shadow: 0 0 1px #444b52;
  display: flex;
  align-items: center;
  background-color: #fff;
  border-left: solid 3px #fff;
  cursor: pointer;

  &.dragging {
    background-color: #f3f9ff;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.13);
    border-left: solid 3px var(--primary);
  }
`;

const HandleWrapper = styled.div`
  display: flex;
  align-items: center;
  svg {
    width: 30px;
    height: 24px;
  }
`;

const PreleadTag = styled.div`
  display: flex;
  align-items: center;
  margin-left: 0.5rem;

  span {
    margin-left: 8px;
    width: 275px;
  }
`;

const TagRectangle = styled.div<{color?: string}>`
  width: 14px;
  height: 14px;
  border-radius: 2px;
  background: ${({color}) => (color ? `${color}` : 'black')};
`;
